// Closed Accounts Archive — opt-in read-only view of Closed facilities.
//
// Added 2026-05-20 for cowork account-mgmt-deep-polish-2026-05-20 (Section B).
//
// Why: the operational AM roster runs off `active_facilities` (Closed/Cancelled
// hidden). My Accounts also has a "Show closed" toggle that lets an AM peek at
// Closed rows inline, but there was no dedicated screen for browsing the
// archive — open feedback like AFB-0002 asked for one. This page reads the
// base `facilities` table and filters `status = 'Closed'`. RLS gates which
// rows a non-superuser can see. Read-only — restore is a manual admin task.
//
// Sort: closed_date DESC NULLS LAST. The handful of rows with NULL closed_date
// (pre-Supabase migration) bucket to the bottom with "Date unknown".
//
// Reachable from a small "Closed Accounts archive →" link at the bottom of
// the My Accounts page. NOT in the sidebar.
// ============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Archive as ArchiveIcon } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { fetchAmNameMap } from '../../shared/am-name';
import { EmptyState, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import { shortAddress } from '../../shared/format';
import { errorMessage } from '../../shared/errors';

interface ClosedFacility {
  facility_id: string;
  facility_name: string | null;
  account_name: string | null;
  market_region: string | null;
  state: string | null;
  account_manager_name: string | null;
  closed_date: string | null;
  close_reason: string | null;
  facility_address: string | null;
}

export default function ClosedAccountsArchive() {
  const [rows, setRows] = useState<ClosedFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Read directly from the base facilities table — `active_facilities`
        // excludes Closed by definition. RLS gates per-role visibility.
        const { data, error: qErr } = await supabase
          .from('facilities')
          .select(
            `
            facility_id, facility_name, market_region, state,
            account_manager_eid, closed_date, close_reason, facility_address,
            accounts!facilities_account_id_fkey ( account_name )
            `,
          )
          .eq('status', 'Closed')
          // PostgREST nullsFirst: false maps to NULLS LAST.
          .order('closed_date', { ascending: false, nullsFirst: false });
        if (qErr) throw qErr;
        if (cancelled) return;
        // Resolve AM display names from account_manager_eid via the canonical
        // v_am_name view (the denormalised facilities.account_manager_name was
        // retired 2026-06-08). One IN-lookup, then client-join in the map below.
        const amNameMap = await fetchAmNameMap(
          (data ?? []).map(
            (raw) =>
              (raw as Record<string, unknown>).account_manager_eid as
                | string
                | null,
          ),
        );
        if (cancelled) return;
        // PostgREST returns embedded relations as either an object (when the
        // FK side is one-to-one) or an array (when it's one-to-many). Newer
        // supabase-js generic types lean toward array. Normalise by treating
        // the row as unknown and reading the embed defensively.
        type EmbedShape =
          | { account_name: string | null }
          | { account_name: string | null }[]
          | null
          | undefined;
        const flat: ClosedFacility[] = (data ?? []).map((raw) => {
          const r = raw as unknown as Record<string, unknown> & { accounts?: EmbedShape };
          const embed = r.accounts;
          const accountName: string | null = Array.isArray(embed)
            ? (embed[0]?.account_name ?? null)
            : (embed?.account_name ?? null);
          return {
            facility_id: r.facility_id as string,
            facility_name: (r.facility_name as string | null) ?? null,
            market_region: (r.market_region as string | null) ?? null,
            state: (r.state as string | null) ?? null,
            account_manager_name:
              amNameMap.get((r.account_manager_eid as string | null) ?? '') ?? null,
            closed_date: (r.closed_date as string | null) ?? null,
            close_reason: (r.close_reason as string | null) ?? null,
            facility_address: (r.facility_address as string | null) ?? null,
            account_name: accountName,
          };
        });
        setRows(flat);
      } catch (e) {
        if (cancelled) return;
        setError(errorMessage(e, 'Failed to load closed accounts.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <Link
        to="/account-management"
        className="inline-flex items-center gap-1 text-xs text-tcs-text-secondary hover:text-tcs-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to My Accounts
      </Link>
      <PageHeader
        eyebrow={<span className="uppercase tracking-wide">Account Management</span>}
        title={
          <span className="inline-flex items-center gap-2">
            <ArchiveIcon className="h-5 w-5 text-tcs-text-secondary" />
            Closed Accounts archive
          </span>
        }
        meta={
          loading
            ? 'Loading…'
            : `${rows.length} closed facilit${rows.length === 1 ? 'y' : 'ies'} visible to your role · sorted by close date, newest first`
        }
      />

      {error && !loading && (
        <ErrorState
          title="Couldn't load the archive"
          body={error}
        />
      )}

      {loading ? (
        <ClosedListSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No closed facilities"
          body="No closed facilities are visible to your account."
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <ClosedRow key={r.facility_id} row={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ClosedRow({ row }: { row: ClosedFacility }) {
  const closeBadge = daysSinceCloseBadge(row.closed_date);
  return (
    <li className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <Link
              to={`/account-management/facilities/${encodeURIComponent(row.facility_id)}`}
              className="text-sm font-semibold text-tcs-text hover:underline"
            >
              {row.facility_name ?? row.facility_id}
            </Link>
            <span className="text-xs text-tcs-text-muted">{row.facility_id}</span>
          </div>
          <div className="mt-0.5 text-xs text-tcs-text-secondary">
            {row.account_name ?? '— no account —'}
            {row.market_region && ` · ${row.market_region}`}
            {row.state && ` · ${row.state}`}
          </div>
          {row.facility_address && (
            <div className="mt-0.5 text-xs text-tcs-text-muted">
              {shortAddress(row.facility_address)}
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-tcs-text-muted">
            <span>Last AM: {row.account_manager_name ?? '—'}</span>
            <span>
              Closed: {formatCloseDate(row.closed_date)}
            </span>
          </div>
          {row.close_reason && (
            <p className="mt-2 rounded bg-tcs-surface px-2 py-1.5 text-xs text-tcs-text-secondary">
              <span className="font-semibold uppercase tracking-wide text-tcs-text-muted">
                Close reason ·{' '}
              </span>
              {row.close_reason}
            </p>
          )}
        </div>
        <div className="shrink-0">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${closeBadge.tone}`}
          >
            {closeBadge.label}
          </span>
        </div>
      </div>
    </li>
  );
}

function ClosedListSkeleton() {
  return (
    <ul className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <li
          key={i}
          className="rounded-xl border border-tcs-border bg-tcs-bg p-4"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 space-y-2">
              <Skeleton width="60%" height={16} block />
              <Skeleton width="40%" height={12} block />
              <Skeleton width="30%" height={11} block />
            </div>
            <Skeleton width={64} height={20} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatCloseDate(d: string | null): string {
  if (!d) return 'Date unknown';
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return 'Date unknown';
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function daysSinceCloseBadge(closedDate: string | null): {
  label: string;
  tone: string;
} {
  if (!closedDate) {
    return { label: 'Date unknown', tone: 'bg-tcs-surface text-tcs-text-muted' };
  }
  const parsed = new Date(closedDate);
  if (Number.isNaN(parsed.getTime())) {
    return { label: 'Date unknown', tone: 'bg-tcs-surface text-tcs-text-muted' };
  }
  const days = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 86400000));
  if (days < 30) return { label: `${days}d ago`, tone: 'bg-st-danger-bg text-st-danger-fg-strong' };
  if (days < 90) return { label: `${days}d ago`, tone: 'bg-st-warn-bg text-st-warn-fg-strong' };
  if (days < 365)
    return { label: `${days}d ago`, tone: 'bg-tcs-surface text-tcs-text-secondary' };
  const years = Math.floor(days / 365);
  return { label: `${years}y ago`, tone: 'bg-tcs-surface text-tcs-text-muted' };
}
